// ═══ AUTH CONTEXT — Manages login state across app ═══
import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);
 
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(api.companyId);

  // Check existing token on mount
  // BY AARYAN:- Verifies authentication and fetches user + company data from API.
  // Normalizes `companyAccess` to ensure it is always a parsed object/array (handles string JSON safely).
  // Prevents runtime errors in UI caused by inconsistent backend response formats.
  useEffect(() => {
    const checkAuth = async () => {
      if (api.token) {
        try {
          // If the stored access token has already expired, refresh FIRST rather than
          // spending a request on a call we know will 401. /auth/refresh returns the
          // user it already loaded, so /auth/me can be skipped entirely in that case.
          const refreshedUser = await api.ensureFreshToken();

          if (!api.token) {
            // Refresh failed; the session is genuinely over.
            api.clearAuth();
          } else {
            // Independent requests: the companies list needs the token and company
            // header, not the user object. These used to run one after the other.
            const [res, coRes] = await Promise.all([
              refreshedUser
                ? Promise.resolve({ success: true, data: refreshedUser })
                : api.getMe(),
              // A companies failure should not sign the user out, same as before.
              api.getCompanies().catch(() => ({ success: false, data: [] })),
            ]);

            if (res.success) {
              const userData = {
                ...res.data,
                companyAccess: typeof res.data.companyAccess === 'string'
                  ? JSON.parse(res.data.companyAccess)
                  : res.data.companyAccess
              };
              setUser(userData);
              if (coRes.success) setCompanies(coRes.data);
            } else {
              api.clearAuth();
            }
          }
        } catch {
          api.clearAuth();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  //BY AARYAN:- Handles user login with safe data normalization and fallback logic.
  // Ensures `companyAccess` is always parsed and assigns a valid companyId if missing.
  // More robust and prevents UI issues caused by inconsistent API responses.
  const login = async (email, password) => {
    const res = await api.login(email, password);
    if (res.success) {
      const userData = {
        ...res.data.user,
        companyAccess: typeof res.data.user.companyAccess === 'string' 
          ? JSON.parse(res.data.user.companyAccess) 
          : res.data.user.companyAccess
      };
      setUser(userData);
      
      const coRes = await api.getCompanies();
      if (coRes.success) {
        setCompanies(coRes.data);
        if (coRes.data.length > 0 && !api.companyId) {
          const firstId = coRes.data[0].id;
          api.setCompanyId(firstId);
          setActiveCompanyId(firstId);
        } else {
          setActiveCompanyId(api.companyId);
        }
      }
    }
    return res;
  };

  const logout = () => {
    api.clearAuth();
    setUser(null);
    setCompanies([]);
    setActiveCompanyId(null);
  };

  const switchCompany = (companyId) => {
    api.setCompanyId(companyId);
    setActiveCompanyId(companyId);
  };

  return (
    <AuthContext.Provider value={{ user, loading, companies, activeCompanyId, login, logout, switchCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
